import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { DeliverableCard, Deliverable, DeliverableStatus } from '../components/DeliverableCard';
import { ArrowLeft, Plus, Calendar, DollarSign, User, Briefcase, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
// Mock Data
const projects = [{
  id: '1',
  name: 'Nike Summer Campaign',
  brand: 'Nike',
  creator: {
    name: 'Sarah Klein',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800'
  },
  deadline: '2024-06-15',
  status: 'active' as const,
  budget: 5000
}, {
  id: '2',
  name: 'Adidas Fitness Series',
  brand: 'Adidas',
  creator: {
    name: 'Lisa Schmidt',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800'
  },
  deadline: '2024-07-01',
  status: 'planning' as const,
  budget: 3000
}, {
  id: '3',
  name: 'Red Bull Event Coverage',
  brand: 'Red Bull',
  creator: {
    name: 'Tom Weber',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800'
  },
  deadline: '2024-06-20',
  status: 'active' as const,
  budget: 8000
}];
const mockDeliverables: Deliverable[] = [{
  id: '1',
  projectId: '1',
  creatorId: '1',
  creatorName: 'Sarah Klein',
  contentType: 'post',
  platform: 'instagram',
  status: 'approved',
  deadline: '2024-06-10',
  description: 'Product showcase post with lifestyle photography'
}, {
  id: '2',
  projectId: '1',
  creatorId: '1',
  creatorName: 'Sarah Klein',
  contentType: 'story',
  platform: 'instagram',
  status: 'in_progress',
  deadline: '2024-06-12',
  description: 'Behind-the-scenes story series (3 slides)'
}, {
  id: '3',
  projectId: '1',
  creatorId: '1',
  creatorName: 'Sarah Klein',
  contentType: 'reel',
  platform: 'instagram',
  status: 'planned',
  deadline: '2024-06-14',
  description: 'Dynamic reel showcasing product features'
}, {
  id: '4',
  projectId: '1',
  creatorId: '1',
  creatorName: 'Sarah Klein',
  contentType: 'video',
  platform: 'youtube',
  status: 'submitted',
  deadline: '2024-06-13',
  description: 'Full product review and unboxing video'
}];
export function ProjectDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [deliverables, setDeliverables] = useState(mockDeliverables);
  const [filter, setFilter] = useState<'all' | DeliverableStatus>('all');
  const project = projects.find(p => p.id === id);
  if (!project) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1246">
        Project not found
      </div>;
  }
  const projectDeliverables = deliverables.filter(d => d.projectId === id);
  const filteredDeliverables = filter === 'all' ? projectDeliverables : projectDeliverables.filter(d => d.status === filter);
  const stats = {
    total: projectDeliverables.length,
    completed: projectDeliverables.filter(d => d.status === 'published').length,
    inProgress: projectDeliverables.filter(d => d.status === 'in_progress' || d.status === 'submitted').length,
    pending: projectDeliverables.filter(d => d.status === 'planned').length
  };
  const handleStatusChange = (deliverableId: string, newStatus: DeliverableStatus) => {
    setDeliverables(prev => prev.map(d => d.id === deliverableId ? {
      ...d,
      status: newStatus
    } : d));
  };
  const statusVariant = project.status === 'active' ? 'success' : project.status === 'planning' ? 'default' : 'secondary';
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-1247">
      <header className="bg-white border-b sticky top-0 z-10" data-id="element-1248">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-1249">
          <div className="flex items-center" data-id="element-1250">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/manager/projects')} data-id="element-1251">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-1252" /> Back to Projects
            </Button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1253">{project.name}</h1>
          </div>
          <Button variant="primary" size="sm" className="bg-blue-600 hover:bg-blue-700" data-id="element-1254">
            <Plus className="h-4 w-4 mr-2" data-id="element-1255" /> Add Deliverable
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1256">
        {/* Project Overview */}
        <Card className="mb-8" data-id="element-1257">
          <CardContent className="p-6" data-id="element-1258">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1259">
              <div className="flex items-center space-x-3" data-id="element-1260">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1261">
                  <Briefcase className="h-5 w-5 text-blue-600" data-id="element-1262" />
                </div>
                <div data-id="element-1263">
                  <p className="text-xs text-gray-500" data-id="element-1264">Brand</p>
                  <p className="font-semibold text-gray-900" data-id="element-1265">{project.brand}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1266">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1267">
                  <User className="h-5 w-5 text-purple-600" data-id="element-1268" />
                </div>
                <div data-id="element-1269">
                  <p className="text-xs text-gray-500" data-id="element-1270">Creator</p>
                  <p className="font-semibold text-gray-900" data-id="element-1271">
                    {project.creator.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1272">
                <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center" data-id="element-1273">
                  <Calendar className="h-5 w-5 text-orange-600" data-id="element-1274" />
                </div>
                <div data-id="element-1275">
                  <p className="text-xs text-gray-500" data-id="element-1276">Deadline</p>
                  <p className="font-semibold text-gray-900" data-id="element-1277">
                    {project.deadline}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1278">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1279">
                  <DollarSign className="h-5 w-5 text-green-600" data-id="element-1280" />
                </div>
                <div data-id="element-1281">
                  <p className="text-xs text-gray-500" data-id="element-1282">Budget</p>
                  <p className="font-semibold text-gray-900" data-id="element-1283">
                    €{project.budget.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t" data-id="element-1284">
              <Badge variant={statusVariant} className="capitalize" data-id="element-1285">
                {project.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Deliverables Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-1286">
          <Card data-id="element-1287">
            <CardContent className="p-6" data-id="element-1288">
              <div className="flex items-center justify-between" data-id="element-1289">
                <div data-id="element-1290">
                  <p className="text-sm text-gray-500" data-id="element-1291">Total Deliverables</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-1292">
                    {stats.total}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1293">
                  <Briefcase className="h-6 w-6 text-blue-600" data-id="element-1294" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1295">
            <CardContent className="p-6" data-id="element-1296">
              <div className="flex items-center justify-between" data-id="element-1297">
                <div data-id="element-1298">
                  <p className="text-sm text-gray-500" data-id="element-1299">Completed</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-1300">
                    {stats.completed}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1301">
                  <CheckCircle2 className="h-6 w-6 text-green-600" data-id="element-1302" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1303">
            <CardContent className="p-6" data-id="element-1304">
              <div className="flex items-center justify-between" data-id="element-1305">
                <div data-id="element-1306">
                  <p className="text-sm text-gray-500" data-id="element-1307">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600" data-id="element-1308">
                    {stats.inProgress}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1309">
                  <Clock className="h-6 w-6 text-blue-600" data-id="element-1310" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1311">
            <CardContent className="p-6" data-id="element-1312">
              <div className="flex items-center justify-between" data-id="element-1313">
                <div data-id="element-1314">
                  <p className="text-sm text-gray-500" data-id="element-1315">Pending</p>
                  <p className="text-2xl font-bold text-gray-600" data-id="element-1316">
                    {stats.pending}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1317">
                  <AlertCircle className="h-6 w-6 text-gray-600" data-id="element-1318" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 mb-6" data-id="element-1319">
          <Button variant={filter === 'all' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('all')} data-id="element-1320">
            All ({stats.total})
          </Button>
          <Button variant={filter === 'planned' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('planned')} data-id="element-1321">
            Planned
          </Button>
          <Button variant={filter === 'in_progress' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('in_progress')} data-id="element-1322">
            In Progress
          </Button>
          <Button variant={filter === 'submitted' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('submitted')} data-id="element-1323">
            Submitted
          </Button>
          <Button variant={filter === 'approved' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('approved')} data-id="element-1324">
            Approved
          </Button>
          <Button variant={filter === 'published' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('published')} data-id="element-1325">
            Published
          </Button>
        </div>

        {/* Deliverables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1326">
          {filteredDeliverables.map(deliverable => <DeliverableCard key={deliverable.id} deliverable={deliverable} onStatusChange={handleStatusChange} data-id="element-1327" />)}
        </div>

        {filteredDeliverables.length === 0 && <Card data-id="element-1328">
            <CardContent className="p-12 text-center" data-id="element-1329">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1330" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1331">
                No deliverables found
              </h3>
              <p className="text-gray-500 mb-6" data-id="element-1332">
                Get started by adding your first deliverable to this project.
              </p>
              <Button variant="primary" className="bg-blue-600 hover:bg-blue-700" data-id="element-1333">
                <Plus className="h-4 w-4 mr-2" data-id="element-1334" /> Add Deliverable
              </Button>
            </CardContent>
          </Card>}
      </main>
    </div>;
}